import type { ReactNode } from 'react'

import { formatDate, formatDateTime } from '@brickup/shared/utils'
import type { DataTableColumn } from './types'
import { cn } from '@/components/base'

/**
 * The cell renderings every table shares, and the row key almost every table
 * wants.
 *
 * A column that reads the same on two screens has to be written the same on
 * both, which is what one set of renderings buys.
 *
 * The row owns the size (`base/Table.tsx`); only `subtitle` sets one, and it
 * steps *down*. A cell setting its own upward leaves one table reading a point
 * larger than the rest, so `no-restricted-syntax` rejects it.
 */

/**
 * The ink a cell is written in, loudest first.
 *
 * The column a row is identified by is `primary`, and everything else picks by
 * how much of the reader's attention it is worth. `mono` and `figure` are the
 * same ramp in a different face, so a monospaced identifier and a
 * right-aligned figure sit on the same four steps.
 */
const INK = {
  /** The column a row is identified by — its name, its address, its title. */
  primary: 'font-medium text-foreground',
  /** What the column exists to show. */
  value: 'text-foreground',
  /** A value beside the one the column is about — a unit, a source, a name. */
  secondary: 'text-foreground-2',
  /** Present, but not what the row is scanned for. */
  muted: 'text-muted-foreground',
} as const

type Ink = keyof typeof INK

const prose = (text: ReactNode, ink: Ink): ReactNode => (
  <span className={cn('truncate', INK[ink])}>{text}</span>
)

export const primary = (text: ReactNode): ReactNode => prose(text, 'primary')
export const value = (text: ReactNode): ReactNode => prose(text, 'value')
export const secondary = (text: ReactNode): ReactNode =>
  prose(text, 'secondary')
export const muted = (text: ReactNode): ReactNode => prose(text, 'muted')

/**
 * The line under a `primary`, where one line does not say enough.
 *
 * Its own step down: `SUBTITLE_CLASS` is sized against the 15/14/13.5px
 * headings, and half a pixel under a 13px row is the hair-difference this file
 * exists to stop.
 */
export const subtitle = (text: ReactNode): ReactNode => (
  <span className="truncate text-xs text-muted-foreground">{text}</span>
)

/** A figure — a right-aligned column lines up on it. */
export const figure = (text: ReactNode, ink: Ink = 'secondary'): ReactNode => (
  <span className={cn('tabular-nums', INK[ink])}>{text}</span>
)

/** An identifier or a path — digits line up, and it is never a sentence. */
export const mono = (text: ReactNode): ReactNode => (
  <span className={cn('block min-w-0 truncate font-mono', INK.muted)}>
    {text}
  </span>
)

/** The row key of anything that already carries one. */
export const keyOf = <T extends { key: string }>(item: T) => item.key

/** The row key of anything the backend gives an id. */
export const idKey = (item: { id: number | string }): string => String(item.id)

/**
 * A timestamp column, at the width four of them settled on.
 *
 * Written out per table, the same column came out `w-44` here and `w-40`
 * there, and one of them printed a raw ISO string.
 */
export function dateColumn<T>(
  key: string,
  label: string,
  options: {
    /** Where the timestamp lives, where the column is keyed differently. */
    get?: (item: T) => Date | string | number | null | undefined
    /** What stands where the row carries no timestamp — „Noch nie
     *  angemeldet“ rather than a dash, where the absence means something. */
    fallback?: string
    /**
     * `date` drops the time of day, for a column read as a date and not as a
     * moment. It carries the narrower width and the right edge with it: what
     * is left is a figure, and figures line up on their last digit.
     */
    format?: 'date' | 'datetime'
  } = {},
): DataTableColumn<T> {
  const get = options.get ?? ((item: T) => (item as Record<string, never>)[key])
  const dateOnly = options.format === 'date'

  return {
    key,
    label,
    width: dateOnly ? 'w-32' : 'w-44',
    align: dateOnly ? 'right' : 'left',
    // A timestamp orders by itself. It takes effect only where the table
    // passes `onSortChange`, so a list nobody sorts is unchanged.
    sortValue: item => {
      const at = get(item)
      return at == null ? null : new Date(at).getTime()
    },
    // A timestamp is a figure, whichever of the two it is drawn as, so the
    // digits line up down the column.
    render: item =>
      figure(
        dateOnly
          ? formatDate(get(item), options.fallback)
          : formatDateTime(get(item), options.fallback),
        'muted',
      ),
  }
}
